import api from '@/lib/api';
import {
  SCRIPT_AUDIO_ALIGN_COURSE_PLAN_TIMEOUT_MS,
  SCRIPT_AUDIO_ALIGN_PREVIEW_TIMEOUT_MS,
} from '@/lib/scriptAudioAlign/apiTimeouts';

export const COURSE_DEPTHS = [
  { value: 'quick', label: 'Quick overview' },
  { value: 'standard', label: 'Standard lesson' },
  { value: 'deep', label: 'Deep dive' },
];

/** Narration / TTS languages for lesson artifact generation. */
export const COURSE_NARRATION_LANGUAGES = [
  { value: 'en', label: 'English', edgeVoice: 'en-US-AriaNeural', murfVoice: 'en-US-natalie' },
  { value: 'hi', label: 'Hindi', edgeVoice: 'hi-IN-SwaraNeural', murfVoice: 'hi-IN-shweta' },
  { value: 'mr', label: 'Marathi', edgeVoice: 'mr-IN-AarohiNeural', murfVoice: 'en-IN-aarav' },
  { value: 'pa', label: 'Punjabi', edgeVoice: 'pa-IN-VaaniNeural', murfVoice: 'en-IN-aarav' },
];

export const COURSE_NARRATION_TONES = [
  { value: 'serious', label: 'Serious', hint: 'Clear classroom tone' },
  { value: 'genz', label: 'Funny GenZ', hint: 'Cool, playful, short punchy lines' },
];

export function courseLanguageMeta(code) {
  return COURSE_NARRATION_LANGUAGES.find((l) => l.value === code) || COURSE_NARRATION_LANGUAGES[0];
}

export const COURSE_STEP_LABELS = {
  analyze_domain: 'Analyze topic domain',
  plan_curriculum: 'Plan curriculum & suggest artifacts',
  compose_script: 'Compose narration script (per subtopic)',
};

export function courseStepLabel(step) {
  const id = String(step?.step_id || '');
  return COURSE_STEP_LABELS[id] || step?.title || id || 'Step';
}

/** Blueprint sections in canonical display order. */
export const BLUEPRINT_SECTION_ORDER = [
  'foundation',
  'understanding',
  'process',
  'practical',
  'assessment',
  'reinforcement',
];

export async function planCourse({ title, notes, audience, depth, storyId }) {
  const { data } = await api.post(
    '/script-audio-align/course/plan',
    {
      title: (title || '').trim(),
      notes: notes || undefined,
      audience: audience || undefined,
      depth: depth || 'standard',
      story_id: storyId || undefined,
    },
    { timeout: SCRIPT_AUDIO_ALIGN_COURSE_PLAN_TIMEOUT_MS },
  );
  return data;
}

export async function startCourseExplain({
  title,
  difficulty,
  curriculum,
  blueprint,
  artifactSelection,
  language = 'en',
  narrationTone = 'serious',
}) {
  const { data } = await api.post(
    '/script-audio-align/course/explain',
    {
      title: (title || '').trim(),
      difficulty: difficulty || 'intermediate',
      curriculum: curriculum || { topics: [], roadmap: [] },
      blueprint: blueprint || { sections: [] },
      artifact_selection: artifactSelection || { choices: [] },
      language: language || 'en',
      narration_tone: narrationTone || 'serious',
    },
    { timeout: 60_000 },
  );
  return data;
}

export async function getCourseExplain(jobId) {
  const { data } = await api.get(`/script-audio-align/course/explain/${jobId}`);
  return data;
}

/**
 * Rebuild the joined narration script the same way the backend does:
 * course title → topic-title bridges → topic/subtopic overview + per-artifact
 * narration. Bridges reuse the plan's own titles so no language is hardcoded.
 */
export function joinExplainNarrationScript(job, courseTitle = '') {
  const units = (job?.units || []).filter((u) => u.status === 'completed');
  const parts = [];
  const title = (courseTitle || job?.title || '').trim();
  if (title) parts.push(`${title.replace(/\.+$/, '')}.`);
  let lastTopic = '';
  for (const unit of units) {
    const topic = String(unit.topic_title || '').trim();
    const subtopic = String(unit.subtopic || '').trim();
    let unitKind = String(unit.unit_kind || '').trim().toLowerCase();
    if (!unitKind) {
      unitKind = topic && (!subtopic || subtopic.toLowerCase() === topic.toLowerCase())
        ? 'topic'
        : 'subtopic';
    }
    let text = String(unit.narration_script || '').trim();
    if (!text) {
      const chunks = [];
      const overview = String(unit.overview_script || '').trim();
      if (overview) chunks.push(overview);
      for (const art of unit.artifacts || []) {
        const spoken = String(art.narration_script || '').trim();
        if (spoken) chunks.push(spoken);
      }
      text = chunks.join('\n\n').trim();
    }
    if (topic && topic.toLowerCase() !== lastTopic.toLowerCase()) {
      if (!text.toLowerCase().startsWith(topic.toLowerCase())) {
        parts.push(`${topic.replace(/\.+$/, '')}.`);
      }
      lastTopic = topic;
    }
    if (unitKind === 'subtopic' && subtopic && subtopic.toLowerCase() !== topic.toLowerCase()) {
      if (!text.toLowerCase().startsWith(subtopic.toLowerCase())) {
        parts.push(`${subtopic.replace(/\.+$/, '')}.`);
      }
    }
    if (text) parts.push(text);
  }
  return parts.join('\n\n').trim();
}

/**
 * Apply course-explain artifacts onto an aligned job with LLM-placed triggers.
 */
export async function applyCourseExplainVisualPlan(alignJobId, explainJobId, { buildPreview = true } = {}) {
  const { data } = await api.post(
    `/script-audio-align/jobs/${alignJobId}/visual-plan/from-course-explain`,
    {
      explain_job_id: explainJobId,
      build_preview: buildPreview !== false,
    },
    { timeout: SCRIPT_AUDIO_ALIGN_PREVIEW_TIMEOUT_MS },
  );
  return data;
}

/** Materialize preview slides into story scenes and return video-editor path. */
export async function openScriptAudioAlignInEditor(jobId) {
  const { data } = await api.post(
    `/script-audio-align/jobs/${jobId}/open-in-editor`,
    {},
    { timeout: 300_000 },
  );
  return data;
}

/** Export synced preview to MP4 on S3; returns download URL. */
export async function exportScriptAudioAlignVideo(jobId) {
  const { data } = await api.post(
    `/script-audio-align/jobs/${jobId}/export-video`,
    {},
    { timeout: 600_000 },
  );
  return data;
}

/** Trigger browser download for an MP4 URL (handles auth-relative paths). */
export async function downloadScriptAudioAlignMp4(videoUrl, filename = 'script-audio-align.mp4') {
  const { mediaUrlForPlayback } = await import('@/lib/mediaUrls');
  const src = mediaUrlForPlayback(videoUrl) || videoUrl;
  const token = typeof localStorage !== 'undefined'
    ? localStorage.getItem('storycraft_token')
    : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(src, { headers, credentials: 'include' });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.mp4') ? filename : `${filename}.mp4`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
